/**
 * Copyright (c) 2021 - Present. Blend Labs, Inc. All rights reserved
 * Blend Confidential - Restricted
 */
import * as React from 'react';
import { FORM_ERROR } from 'final-form';
import { Field, Form } from 'react-final-form';
import { Modal, ActionButton, AlertBar, Label, LinkButton, LinkButtonWrapper } from '@blend/enterprise-components';
import { css, StyleSheet } from 'aphrodite';
import { useParams } from 'react-router-dom';
import { useState, useContext } from 'react';

import { ModalContent } from 'blb-components/ModalContent';
import { DOCUMENT_DEFINITIONS } from 'blb-constants/DocumentTypes';
import { MeContext } from 'blb-contexts/Me';
import { BlendBridge } from 'blb-types/blendBridge';
import { errorMessage } from 'blb-utils/errors';

import { AttachmentRepeater } from './AttachmentRepeater';
import { subjectInputConfig, recipientInputConfig, topicInputConfig, messageInputConfig } from './inputConfigs';
import type { ReduxProps } from './NewThreadModalContainer';
import type { Attachment } from '../../types';
import { formatUserForMessage } from '../../utils/threadUtils';
import { DocUploadModal } from '../../../DocUploadModal';

const styles = StyleSheet.create({
  attachButton: {
    marginLeft: 85,
  },
  attachmentWrapper: {
    marginBottom: 14,
    marginLeft: 85,
  },
  composerLabel: {
    width: 100,
  },
  fieldWrapper: {
    alignItems: 'center',
    flexBasis: '100%',
    flexDirection: 'row',
    justifyContent: 'space-between',
  },
  footerBar: {
    display: 'flex',
    justifyContent: 'flex-end',
    marginTop: 28,
  },
  row: {
    ':not(:last-child)': {
      marginBottom: 14,
    },
    display: 'flex',
    justifyContent: 'space-between',
  },
  spacer: {
    ':not(:last-child)': {
      marginBottom: 24,
    },
  },
});

interface NewThreadForm {
  message: string;
  subject: string;
  topic: BlendBridge.API.Topic;
}

export namespace NewThreadModel {
  export interface Props {
    handleClose: () => void;
    isOpen: boolean;
    tenant: string;
  }
}

export const NewThreadModal: React.FC<NewThreadModel.Props & ReduxProps> = ({
  isOpen,
  handleClose,
  createThreadForLoan,
  user,
  tenant,
}) => {
  const { loanId } = useParams<{ loanId: string }>();
  const [attachmentModalOpen, setAttachmentModalOpen] = useState<boolean>(false);
  const [attachments, setAttachments] = useState<Attachment[]>([]);
  const me = useContext(MeContext);

  const generateThread = (values: NewThreadForm) => {
    const { message, subject, topic } = values;
    return {
      attachments,
      body: message,
      subject,
      tenant,
      topic,
      user: formatUserForMessage(user),
    };
  };

  const sendMessage = async (values: NewThreadForm) => {
    // we are trying to error handle API requests at the action level
    // we're leaving this one in here for now and have react-final-form handle the error
    // to keep it consistent with how we handle forms. it is worth revisiting down the line
    try {
      const thread = generateThread(values);
      await createThreadForLoan(loanId, thread);
      setAttachments([]);
      handleClose();
    } catch (error) {
      return {
        [FORM_ERROR]: errorMessage('SENDING_MESSAGE', me?.principalType),
      };
    }
  };

  const handleUploadSuccess = (documents: Attachment[]) => {
    setAttachments(documents);
  };

  const handleRemoveAttachment = (index: number) => {
    const items = attachments.slice();
    items.splice(index, 1);
    setAttachments(items);
  };

  const handleCloseModal = () => {
    setAttachments([]);
    handleClose();
  };

  return (
    <>
      <Modal contentLabel="New Message" handleClose={handleCloseModal} isOpen={isOpen}>
        <ModalContent handleClose={handleCloseModal} headerText="New Message">
          <Form initialValues={{ recipient: 'LOAN_OFFICER' }} onSubmit={sendMessage}>
            {({ handleSubmit, submitError, submitting }) => {
              return (
                <div>
                  {submitError && (
                    <div className={css(styles.spacer)}>
                      <AlertBar id="new-thread-error" message={submitError} theme="error" />
                    </div>
                  )}
                  <div className={css(styles.row)}>
                    <Label className={css(styles.composerLabel)} labelSize="md">
                      To
                    </Label>
                    <div className={css(styles.fieldWrapper)}>
                      <Field {...recipientInputConfig} />
                    </div>
                  </div>
                  <div className={css(styles.row)}>
                    <Label className={css(styles.composerLabel)} labelSize="md">
                      Topic
                    </Label>
                    <div className={css(styles.fieldWrapper)}>
                      <Field {...topicInputConfig} />
                    </div>
                  </div>
                  <div className={css(styles.row)}>
                    <Label className={css(styles.composerLabel)} labelSize="md">
                      Subject
                    </Label>
                    <div className={css(styles.fieldWrapper)}>
                      <Field {...subjectInputConfig} />
                    </div>
                  </div>
                  <div className={css(styles.row)}>
                    <Label className={css(styles.composerLabel)} labelSize="md">
                      Message
                    </Label>
                    <div className={css(styles.fieldWrapper)}>
                      <Field {...messageInputConfig} />
                    </div>
                  </div>
                  {attachments.length > 0 && (
                    <div className={css(styles.attachmentWrapper)}>
                      <AttachmentRepeater
                        attachments={attachments}
                        handleRemove={handleRemoveAttachment}
                        singleItemPerRow
                      />
                    </div>
                  )}

                  <div className={css(styles.row, styles.attachButton)}>
                    <LinkButtonWrapper>
                      <LinkButton
                        btnSize="md"
                        data-it="NewThreadModal_AttachFilesButton"
                        icon="attach_file"
                        iconPosition="left"
                        label="Attach files"
                        onClick={() => setAttachmentModalOpen(true)}
                        theme="primary"
                      />
                    </LinkButtonWrapper>
                  </div>
                  <div className={css(styles.footerBar)}>
                    <ActionButton
                      id="NewThreadModal-submit"
                      isLoading={submitting}
                      label="Send"
                      onClick={handleSubmit}
                    />
                  </div>
                </div>
              );
            }}
          </Form>
        </ModalContent>
      </Modal>
      <DocUploadModal
        allowMultipleUploads={true}
        handleClose={() => setAttachmentModalOpen(false)}
        isOpen={attachmentModalOpen}
        onSuccess={handleUploadSuccess}
        selectableDocTypes={DOCUMENT_DEFINITIONS}
        title="Attach Documents"
      />
    </>
  );
};
